import { BaseModal, BaseModalProps } from './BaseModal';
import { Box } from '@mui/material';
import ThemeButton from '../common/ThemeButton';
import { CardLayout } from '../layouts/CardLayout';
import { InputsLayout } from '../layouts/InputsLayout';
import { DatePicker } from '../common/DatePicker';
import { ChangeEvent, useEffect, useState } from 'react';
import { useAddEmploymentScheduleToEmployee } from '../../api/EmployeeService/useAddEmploymentScheduleToEmployee';
import { useGetEmploymentSchedules } from '../../api/EmployeeService/useGetEmploymentSchedules';
import { useAlert } from '../../contexts/AlertContext';
import { Dropdown } from '../common/Dropdown';
import { useParams } from 'react-router-dom';
import { AddEmploymentScheduleToEmployeeRequest } from '../../types/backendRequests/AddEmploymentScheduleToEmployeeRequest';
import { useModal } from '../../contexts/ModalContext';
import { EmploymentSchedule } from '../../types/backendResponses/EmploymentSchedule';
import { theme } from '../../configs/theme';

export default function AddEmploymentScheduleModal({ name }: BaseModalProps) {
  const { employeeId } = useParams<{ employeeId: string }>();
  const { setModalUpdated, close } = useModal();
  const { alert } = useAlert();

  // API hooks
  const { error: addScheduleError, action: addEmploymentSchedule } =
    useAddEmploymentScheduleToEmployee();
  const {
    data: employmentSchedules,
    error: employmentSchedulesError,
    action: getEmploymentSchedules,
    isLoading: isGettingEmploymentSchedules,
  } = useGetEmploymentSchedules();

  const [scheduleTypes, setScheduleTypes] = useState<{ title: string; value: string }[]>([]);
  const [request, setRequest] = useState<{
    startDate: string;
    scheduleId: string;
    employeeId: string;
  }>({
    startDate: '',
    scheduleId: '',
    employeeId: employeeId!,
  });

  const mapSchedulesToDropdownValues = (data: EmploymentSchedule[]) => {
    return data.map((e) => {
      return {
        value: e.id,
        title: e.name,
      };
    });
  };

  useEffect(() => {
    const fetchEmploymentSchedules = async () => {
      const success = await getEmploymentSchedules();
      if (!success) {
        console.error('Error fetching employment schedules:', employmentSchedulesError);
      }
    };
    fetchEmploymentSchedules();
  }, []);

  useEffect(() => {
    if (!employmentSchedules || employmentSchedules.length === 0) {
      return;
    }

    setScheduleTypes(mapSchedulesToDropdownValues(employmentSchedules));

    // set the default values to the request and show in inputfields
    setRequest((prev: any) => ({
      ...prev,
      scheduleId: mapSchedulesToDropdownValues(employmentSchedules)[0].value,
    }));
  }, [employmentSchedules]);

  const submit = async () => {
    // filter out the selected employment schedule
    const mappedRequest: AddEmploymentScheduleToEmployeeRequest = {
      ...request,
      employmentSchedule: employmentSchedules?.find(
        (e) => e.id === request.scheduleId
      ) as EmploymentSchedule,
    };

    const success = await addEmploymentSchedule(mappedRequest);
    if (success) {
      // prompt user
      alert('success', 'Schema tillagt på anställd');
      // trigger update state in order to later refresh data on page
      setModalUpdated(true);
      close('AddEmploymentScheduleModal');
    } else {
      if (
        addScheduleError?.response?.data?.message ===
        'date of new employment schedule must be after start date of last employment schedule'
      ) {
        alert('warning', 'Nya schemat måste börja efter föregående schemats startdatum');
        return;
      }
      if (
        addScheduleError?.response?.data?.message ===
        'schedule type of new employment schedule is the same as the last employment schedule'
      ) {
        alert('warning', 'Nya schemat är samma som senaste schemat');
        return;
      }
    }
  };

  const handleOnChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldName: string
  ) => {
    setRequest((prev: any) => ({ ...prev, [fieldName]: e.target.value }));
  };

  return (
    <BaseModal title="Redigera anställningsschema" name="AddEmploymentScheduleModal">
      <CardLayout>
        <InputsLayout>
          <Dropdown
            name="employmentScheduleId"
            value={request.scheduleId}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleOnChange(e, 'scheduleId')}
            data={scheduleTypes}
            wrapperProps={{
              label: 'Anställningsschema',
            }}
          />
          <DatePicker
            wrapperProps={{ label: 'Datum' }}
            name="date"
            value={request.startDate}
            onChange={(e) => handleOnChange(e, 'startDate')}
          />
        </InputsLayout>
      </CardLayout>
      <Box
        sx={{
          display: 'flex',
          flexFlow: 'row-reverse',
          padding: '0.5rem',
          position: 'sticky',
          bottom: 0,
          backgroundColor: theme.palette.customColors.cultured,
        }}
      >
        <ThemeButton onClick={() => submit()}>Lägg till</ThemeButton>
      </Box>
    </BaseModal>
  );
}
